/**
 * ResourceEditor Component
 *
 * Tabbed-modal editing shell — the editing counterpart to `TabbedDetailsModal`.
 * Provides a sticky header, a tab rail with per-tab error badges, a scrolling
 * body that renders only the active tab, and a persistent Save/Cancel footer.
 *
 * Unlike a wizard, every tab is reachable at any time and Save is always
 * available. On an invalid submit, the call site should move to the first tab
 * that owns an error (see `firstTabWithError`).
 *
 * @example
 * const [activeTab, setActiveTab] = useState('general');
 * const onInvalid = (errs) => {
 *   const t = firstTabWithError(errs, TABS);
 *   if (t) setActiveTab(t);
 * };
 * <ResourceEditor
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title={editing ? 'Edit SIP Peer' : 'Add SIP Peer'}
 *   tabs={TABS}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   errors={form.formState.errors}
 *   onSubmit={form.handleSubmit(onSubmit, onInvalid)}
 *   onCancel={onClose}
 *   isSaving={mutation.isPending}
 *   saveLabel={editing ? 'Update Peer' : 'Create Peer'}
 * />
 */

import React, { useEffect } from 'react';
import { X, type LucideIcon } from 'lucide-react';
import type { FieldErrors, FieldValues } from 'react-hook-form';
import { tabHasError } from './resourceEditorErrors';

export interface ResourceEditorTab {
  /** Stable tab id (used for active state + error mapping). */
  id: string;
  /** Tab label shown in the rail. */
  label: string;
  /** Optional icon (Lucide only, per package guidance). */
  icon?: LucideIcon;
  /** Optional short helper under the label. */
  description?: string;
  /**
   * RHF field paths owned by this tab. Used to derive the per-tab error badge.
   * Dotted paths act as prefixes: `'metadata'` matches `'metadata.userAgent'`.
   */
  fields: ReadonlyArray<string>;
  /** Hide the tab entirely (e.g. TLS tab when transport !== 'TLS'). */
  hidden?: boolean;
  /** Tab body renderer. Reads/writes the shared RHF form via closure. */
  render: () => React.ReactNode;
}

export interface ResourceEditorProps {
  /** Whether the editor is open. */
  isOpen: boolean;
  /** Close handler (backdrop / escape / X button). */
  onClose: () => void;

  /** Modal title, e.g. "Edit SIP Peer". */
  title: string;
  /** Optional subtitle under the title. */
  subtitle?: string;
  /** Optional header icon element. */
  headerIcon?: React.ReactNode;
  /** Optional banner rendered under the header (e.g. contextual notes). */
  headerBanner?: React.ReactNode;

  /** Tab configuration. */
  tabs: ReadonlyArray<ResourceEditorTab>;
  /** Currently active tab id. */
  activeTab: string;
  /** Tab change handler. */
  onTabChange: (tabId: string) => void;

  /** RHF errors — drives the per-tab error badges. */
  errors?: FieldErrors<FieldValues>;

  /** Wire to `form.handleSubmit(onValid, onInvalid)` at the call site. */
  onSubmit: (e: React.FormEvent) => void;
  /** Cancel handler (footer button). Defaults to `onClose` when omitted. */
  onCancel?: () => void;

  /** Disables Save and shows a busy label while true. */
  isSaving?: boolean;
  /** Save button label. Default: "Save". */
  saveLabel?: string;
  /** Busy Save button label. Default: "Saving...". */
  savingLabel?: string;
  /** Cancel button label. Default: "Cancel". */
  cancelLabel?: string;

  /** Optional extra footer content on the left (e.g. a delete button). */
  footerStart?: React.ReactNode;

  /**
   * Whether clicking the backdrop closes the editor. Defaults to false: these
   * editors hold multi-tab form state, and a stray click outside should not
   * silently discard edits. Escape still closes (deliberate keypress).
   */
  closeOnBackdrop?: boolean;
  /** Whether pressing Escape closes the editor (default: true). */
  closeOnEscape?: boolean;
}

export function ResourceEditor({
  isOpen,
  onClose,
  title,
  subtitle,
  headerIcon,
  headerBanner,
  tabs,
  activeTab,
  onTabChange,
  errors,
  onSubmit,
  onCancel,
  isSaving = false,
  saveLabel = 'Save',
  savingLabel = 'Saving...',
  cancelLabel = 'Cancel',
  footerStart,
  closeOnBackdrop = false,
  closeOnEscape = true,
}: ResourceEditorProps) {
  // Escape to close.
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  // Lock body scroll while open.
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const visibleTabs = tabs.filter((tab) => !tab.hidden);
  const active =
    visibleTabs.find((tab) => tab.id === activeTab) ?? visibleTabs[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        className="relative flex h-[80vh] w-full max-w-4xl flex-col rounded-xl bg-white"
        role="dialog"
        aria-modal="true"
        aria-labelledby="resource-editor-title"
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 p-6">
          <div className="flex items-center gap-3">
            {headerIcon}
            <div>
              <h2
                id="resource-editor-title"
                className="text-xl font-semibold text-gray-900"
              >
                {title}
              </h2>
              {subtitle && (
                <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close editor"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {headerBanner && (
          <div className="flex-shrink-0 border-b border-gray-200">
            {headerBanner}
          </div>
        )}

        {/* Tab rail */}
        <div className="flex-shrink-0 overflow-x-auto border-b border-gray-200 bg-gray-50 px-4 py-3">
          <div
            className="flex min-w-max items-center gap-2"
            role="tablist"
            aria-label={`${title} settings`}
          >
            {visibleTabs.map((tab) => {
              const isActive = active?.id === tab.id;
              const hasError = tabHasError(errors, tab.fields);
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => onTabChange(tab.id)}
                  className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-left transition-colors ${
                    isActive
                      ? 'border-blue-500 bg-white text-blue-700 shadow-sm'
                      : 'border-transparent text-gray-600 hover:border-gray-200 hover:bg-white'
                  }`}
                >
                  {TabIcon && (
                    <TabIcon
                      className={`h-4 w-4 flex-shrink-0 ${
                        isActive ? 'text-blue-600' : 'text-gray-400'
                      }`}
                    />
                  )}
                  <span>
                    <span className="block text-sm font-medium">
                      {tab.label}
                    </span>
                    {tab.description && (
                      <span className="block text-xs text-gray-500">
                        {tab.description}
                      </span>
                    )}
                  </span>
                  {hasError && (
                    <span
                      className="ml-1 h-2 w-2 flex-shrink-0 rounded-full bg-red-500"
                      aria-label="This tab has validation errors"
                      title="This tab has validation errors"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Body + footer share one form so the persistent Save button submits it. */}
        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto p-6">{active?.render()}</div>

          {/* Footer */}
          <div className="flex flex-shrink-0 items-center justify-between border-t border-gray-200 bg-gray-50 p-6">
            <div className="flex items-center">{footerStart}</div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onCancel ?? onClose}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {cancelLabel}
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving ? savingLabel : saveLabel}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
